import type { LayoutShell } from '../zod.js';
import type { WizardOptionCopy } from './types.js';

/**
 * Read-aloud copy for each Layout Shell. Used by LayoutStep + the printable
 * client leave-behind.
 *
 * Includes a special 'auto' entry for the Auto-mode card.
 */

export const AUTO_SHELL_COPY: WizardOptionCopy = {
  name: 'Auto',
  oneLiner: 'Pick the right shell for me from the chosen vibe.',
  clientPitch:
    "Most clients should leave this on Auto. We've already paired every vibe with the layout shell that best amplifies it - so picking 'The Wellness' gets you a centered Atrium hero, picking 'The Bold' gets you a Marquee manifesto. Change vibe and the layout follows. You only need to override this if there's a specific structural feel you want that doesn't match the vibe default.",
  bestFor: ['First-time clients', 'When vibe + structure should match', 'When you want to see what we recommend'],
  avoidIf: ['You have strong opinions about composition specifically', 'You want to mix moods (e.g. Wellness colors with a Workshop layout)'],
  feel: ['Confident', 'Curated', 'No-decision-fatigue'],
};

export const SHELL_COPY: Readonly<Record<LayoutShell, WizardOptionCopy>> = {
  stack: {
    name: 'N°01 - Stack',
    oneLiner: 'Vertical narrative with cards that peel over the section above.',
    clientPitch:
      "This is the reliable default. Sections stack top to bottom in a clear narrative, and cards lift slightly to break the boundary between them - so the page feels composed, not segmented. It works for almost any business and never gets in the way of the content.",
    bestFor: ['Service businesses', 'Trades', 'Anyone unsure which shell fits', 'Most use cases honestly'],
    avoidIf: ['You want the structure itself to be a statement'],
    feel: ['Composed', 'Reliable', 'Clear'],
  },
  atrium: {
    name: 'N°02 - Atrium',
    oneLiner: 'A centered serif italic statement that feels like walking into a lobby.',
    clientPitch:
      "Picture your homepage as the entrance of a beautifully designed building. One italic serif line lands in the middle of the screen - your point of view, your promise - and everything else fans out below it in a soft staggered grid. It gives visitors a moment to breathe before you start showing them what you do.",
    bestFor: ['Spas', 'Coaches', 'Wellness brands', 'Boutique hotels', 'Anyone selling a feeling, not a feature'],
    avoidIf: ['You need to lead with hard product specs', 'Your business depends on dense above-the-fold info'],
    feel: ['Welcoming', 'Composed', 'Quietly confident'],
  },
  ledger: {
    name: 'N°03 - Ledger',
    oneLiner: 'Editorial spread - type-led hero, multi-column body, sidebar metadata.',
    clientPitch:
      "This shell reads like a premium magazine spread. The hero is mostly type, the body splits into multiple columns with a sidebar of metadata, and hairline rules separate every section like an editor laid them out by hand. People will read longer here than they would on a standard site.",
    bestFor: ['Authors', 'Editorial-led brands', 'Premium consultants', 'Long-form professional services'],
    avoidIf: ['Your audience scans, never reads', 'You need to demo a product visually'],
    feel: ['Premium', 'Authored', 'Deliberate'],
  },
  vitrine: {
    name: 'N°04 - Vitrine',
    oneLiner: 'Gallery-first - the hero IS a grid of masked photos floating in a leak field.',
    clientPitch:
      "Your work IS the pitch. Instead of a headline-and-button hero, we open with a gallery of your best photography masked into soft organic shapes, floating over a colored leak. People see your portfolio in the first three seconds, before they read a word.",
    bestFor: ['Photographers', 'Restaurants', 'Salons', 'Retail', 'Anyone whose product photographs well'],
    avoidIf: ['You don\'t have great photography yet', 'Your value is invisible (consulting, advice)'],
    feel: ['Visual', 'Confident', 'Generous'],
  },
  workshop: {
    name: 'N°05 - Workshop',
    oneLiner: 'Maximum intentional clutter - cards, labels, photos, stats above the fold.',
    clientPitch:
      "This shell looks like a well-organized workbench. Multiple cards, labels, small stats and photos all visible at once, off the central axis - it feels like there's a lot going on because there IS a lot going on, and you're proud of all of it. It's the opposite of the empty hero with one button.",
    bestFor: ['Trades', 'Artisans', 'Multi-service shops', 'Anyone proud of their tools and process'],
    avoidIf: ['You sell quiet luxury', 'Your audience is intimidated by visual density'],
    feel: ['Generous', 'Lived-in', 'Productive'],
  },
  marquee: {
    name: 'N°06 - Marquee',
    oneLiner: 'A giant display headline fills the viewport like a manifesto poster.',
    clientPitch:
      "The hero IS your manifesto. One huge sentence fills the screen, set in a heavy display face that demands you read it. Photos float small around it, secondary. The first interaction with your brand is a statement - not a feature list, not a CTA, a point of view.",
    bestFor: ['Bold brands', 'Statement businesses', 'Anyone selling a worldview', 'Performance / fitness'],
    avoidIf: ['You need a feature comparison up top', 'Your audience expects formal corporate'],
    feel: ['Loud', 'Opinionated', 'Memorable'],
  },
  drift: {
    name: 'N°07 - Drift',
    oneLiner: 'Multi-layer parallax hero with animated leaks. Feels alive.',
    clientPitch:
      "This shell uses motion as a feature. Photos drift slower than the text when you scroll, the colored leaks pulse softly, and the hero is built in layers that move at different speeds. The site feels alive, not static. It's the most theatrical option that still works for a real business.",
    bestFor: ['Hospitality', 'Events', 'Entertainment', 'Tech with attitude', 'Hospitality + travel'],
    avoidIf: ['Your audience disables animation', 'You need a snappy utilitarian feel'],
    feel: ['Alive', 'Cinematic', 'Atmospheric'],
  },
  cabinet: {
    name: 'N°08 - Cabinet',
    oneLiner: 'Dense tile-grid first - tiny hero, services laid out like a curated cabinet.',
    clientPitch:
      "This shell skips the big hero and opens with a curated grid of your offerings. Each tile is a service or product, laid out like specimens in a beautiful cabinet. People who want to browse can browse; people who want to drill in can click. It works best when you have multiple distinct things to sell.",
    bestFor: ['Productized services', 'Portfolios', 'Multi-offering businesses', 'Studios with packages'],
    avoidIf: ['You have one main thing to sell', 'You need to lead with a story'],
    feel: ['Organized', 'Curated', 'Browsable'],
  },
  letterhead: {
    name: 'N°09 - Letterhead',
    oneLiner: 'Maximum restraint - mostly type, mostly negative space, one masked pill photo.',
    clientPitch:
      "This shell whispers. Mostly typography, lots of empty space, one carefully masked photo. It's what a high-end consulting firm or a couture atelier would build if they wanted you to feel they were being deliberate with every pixel. People will assume you're expensive in the best way.",
    bestFor: ['High-end consulting', 'Law firms at the top of the market', 'Bespoke design studios', 'Couture / atelier'],
    avoidIf: ['You need to lead with visuals', 'Your audience expects density'],
    feel: ['Restrained', 'Confident', 'Premium'],
  },
  foyer: {
    name: 'N°10 - Foyer',
    oneLiner: 'Split hero - type on the left, masked photo on the right. The most balanced default.',
    clientPitch:
      "This shell splits the hero in half - your message on one side, a masked photo on the other, slightly off-balance so it doesn't feel like a template. It's the most universally flattering structure: clear, premium, friendly without being soft.",
    bestFor: ['Professional services', 'B2B agencies', 'Anyone who wants premium without theatrics'],
    avoidIf: ['You want the structure to be a statement', 'You don\'t have a hero photo'],
    feel: ['Balanced', 'Welcoming', 'Premium'],
  },
  portico: {
    name: 'N°11 - Portico',
    oneLiner: 'Heaviest blueprint labeling - numbered sections with exposed hairline rules.',
    clientPitch:
      "This shell is for businesses that respect engineering. Every section is numbered like an architect's drawing - N°01, N°02 - with exposed hairline rules and tick marks. It signals that you're meticulous, that you measure things, that there's a process behind your work.",
    bestFor: ['Engineering', 'Contracting', 'Heritage trades', 'Anyone selling craft + precision', 'Architects'],
    avoidIf: ['Your brand voice is casual / playful', 'Your audience finds technical aesthetics intimidating'],
    feel: ['Engineered', 'Meticulous', 'Honest'],
  },
  cascade: {
    name: 'N°12 - Cascade',
    oneLiner: 'Cinematic top-down reveal with strong peel + drift between every section.',
    clientPitch:
      "This shell is the most theatrical option. Every section reveals as you scroll, photos drift, cards peel over the section above with conviction. It feels like a film opening, not a business website. Use it when you're selling an experience, not a feature.",
    bestFor: ['Story-led brands', 'Hospitality', 'Events', 'Anything immersive', 'Travel'],
    avoidIf: ['You need utilitarian / quick info access', 'Your audience disables animation'],
    feel: ['Cinematic', 'Theatrical', 'Immersive'],
  },
};
